import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Edit2, Trash2, Target, Calendar, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { FinancialGoal, GoalStatus } from '@/hooks/useGoals';
import { formatCurrency } from '@/lib/foco-calculations';
import { cn } from '@/lib/utils';

interface GoalCardProps {
  goal: FinancialGoal;
  onEdit: () => void;
  onDelete: () => void;
}

export function GoalCard({ goal, onEdit, onDelete }: GoalCardProps) {
  const progress = goal.target_amount > 0 
    ? Math.min((Number(goal.current_amount) / Number(goal.target_amount)) * 100, 100)
    : 0;

  const statusConfig: Record<GoalStatus, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
    active: { label: 'Activa', variant: 'default' },
    completed: { label: 'Completada', variant: 'secondary' },
    paused: { label: 'Pausada', variant: 'outline' },
  };

  const priorityLabels: Record<number, string> = {
    1: 'Máxima',
    2: 'Alta',
    3: 'Media',
    4: 'Baja',
    5: 'Mínima',
  };

  return (
    <div className="rounded-xl border bg-card p-5 space-y-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <Target className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">{goal.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={statusConfig[goal.status].variant} className="text-xs">
                {statusConfig[goal.status].label}
              </Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Flag className="h-3 w-3" />
                Prioridad {priorityLabels[goal.priority]}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Progreso</span>
          <span className="font-medium">{progress.toFixed(0)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {formatCurrency(Number(goal.current_amount))}
          </span>
          <span className="font-medium text-primary">
            {formatCurrency(Number(goal.target_amount))}
          </span>
        </div>
      </div>

      {goal.deadline && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>
            Fecha límite: {format(new Date(goal.deadline), 'd MMM yyyy', { locale: es })}
          </span>
        </div>
      )}
    </div>
  );
}
